<template>
  <v-container fluid>
    <MessagePopup :text="errorMessage" color="error"/>
    <v-stepper v-model="step">
      <v-stepper-header>
        <v-stepper-step :complete="step > 1" :step="1" editable>Load your file</v-stepper-step>
        <v-divider></v-divider>
        <v-stepper-step :complete="step > 2" :step="2" :editable="step > 2">Import the content</v-stepper-step>
        <v-divider></v-divider>
        <v-stepper-step :complete="step > 3" :step="3" :editable="step > 3">Search products</v-stepper-step>
        <v-divider></v-divider>
        <v-stepper-step :step="4">Get result</v-stepper-step>
      </v-stepper-header>

      <v-stepper-items>
        <v-stepper-content step="1">
          <v-alert type="error" v-if="errorLoading">{{ errorLoading }}</v-alert>
          <v-container>
            <v-row justify="center">
              <v-col cols="12" sm="8" md="6" lg="4">
                <p>
                  Load your Excel file by clicking on the file input below.
                </p>
                <v-file-input
                  label="Click to load your excel file"
                  clearable
                  @change="loadFile"
                  v-model="file"
                ></v-file-input>
              </v-col>
            </v-row>
          </v-container>
        </v-stepper-content>
        <v-stepper-content step="2">
          <p>
            Select corresponding columns to go to next step.
          </p>
          <v-simple-table>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-center">Remove</th>
                  <th v-for="col in fileContentCols" :key="col" style="font-size: 12px;">
                    <v-checkbox
                      class="mt-0"
                      label="PN"
                      v-model="fileColumnMapping.partNumber"
                      :value="col"
                      hide-details
                    ></v-checkbox>
                    <v-checkbox
                      class="mt-0"
                      label="Quantity"
                      v-model="fileColumnMapping.quantity"
                      :value="col"
                      hide-details
                    ></v-checkbox>
                    <v-checkbox
                      class="mt-0"
                      label="Cust PN"
                      v-model="fileColumnMapping.customerPartNumber"
                      :value="col"
                      hide-details
                    ></v-checkbox>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(line, lineKey) in fileContentSanitized" :key="lineKey">
                  <td>
                    <v-btn
                      v-if="lineKey > 0"
                      small
                      icon
                      color="error"
                      @click="fileContent.splice(0, lineKey)"
                    >
                      <v-icon small>mdi-transfer-up</v-icon>
                    </v-btn>
                    <v-btn v-else icon small disabled></v-btn>
                    <v-btn
                      small
                      icon
                      color="error"
                      @click="fileContent.splice(lineKey, 1)"
                    >
                      <v-icon small>mdi-close-circle</v-icon>
                    </v-btn>
                    <v-btn
                      v-if="lineKey < (fileContentSanitized.length - 1)"
                      small
                      icon
                      color="error"
                      @click="fileContent.splice(lineKey + 1)"
                    >
                      <v-icon small>mdi-transfer-down</v-icon>
                    </v-btn>
                    <v-btn v-else icon small disabled></v-btn>
                  </td>
                  <td v-for="(value, valueKey) in line" :key="valueKey">
                    {{ value }}
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <p class="text-right">
            <v-btn
              color="primary"
              :disabled="fileColumnMapping.partNumber === null || fileColumnMapping.quantity === null"
              @click="createBom"
            >
              Done
            </v-btn>
          </p>
        </v-stepper-content>
        <v-stepper-content step="3">
          <h3>Parameters</h3>
          <v-container fluid>
            <v-row>
              <v-col cols="4" md="4" lg="2">
                <v-text-field
                  type="number"
                  class="mt-0 pt-0"
                  v-model="multiplier"
                  label="multiplier"
                ></v-text-field>
              </v-col>
              <v-col cols="8" md="8" lg="3">
                <v-slider
                  class="mt-4"
                  thumb-label="always"
                  min="0"
                  max="20"
                  v-model="multiplierLog"
                >
                  <template v-slot:thumb-label>
                    {{ multiplierString }}
                  </template>
                </v-slider>
              </v-col>
              <v-col cols="12" sm="6" md="4" lg="4" xl="2">
                <v-radio-group v-model="priceOnly" row>
                  <v-radio
                    label="Price only"
                    :value="true"
                  ></v-radio>
                  <v-radio
                    label="At date"
                    :value="false"
                  ></v-radio>
                </v-radio-group>
              </v-col>
              <v-col cols="12" sm="6" md="4" lg="2">
                <v-menu
                  v-model="menuDateExpected"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="dateExpected"
                      label="Shipping date"
                      prepend-icon="mdi-calendar"
                      readonly
                      v-on="on"
                      :disabled="priceOnly"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="dateExpected"
                    @input="menuDateExpected = false"
                    no-title
                  ></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>
          </v-container>
          <h3>Suppliers analysis</h3>
          <v-container fluid>
            <v-row>
              <v-col cols="12" xl="6">
                <v-simple-table>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-center">Supplier</th>
                        <th class="text-center">
                          <v-btn v-if="preferredSuppliers.length === suppliers.filter(supplier => supplier.enabled).length" icon small @click="preferredSuppliers = []">
                            <v-icon small>mdi-checkbox-marked</v-icon>
                          </v-btn>
                          <v-btn v-else icon small @click="preferredSuppliers = suppliers.filter(supplier => supplier.enabled).map(supplier => supplier.id)">
                            <v-icon v-if="preferredSuppliers.length === 0" small>mdi-checkbox-blank-outline</v-icon>
                            <v-icon v-else small>mdi-checkbox-intermediate</v-icon>
                          </v-btn>
                          Only
                        </th>
                        <th class="text-center">Price modifier</th>
                        <th class="text-center">Responses</th>
                        <th class="text-center">Selected</th>
                        <th class="text-center">Net price</th>
                        <th class="text-center">Excess</th>
                        <th class="text-center">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, key) in supplierArticles" :key="key">
                        <td>{{ item.supplier.name }}<CopyToDGKCart :item="item"/></td>
                        <td class="text-center">
                          <v-btn v-if="preferredSuppliers.includes(item.supplier.id)" icon small @click="preferredSuppliers.splice(preferredSuppliers.indexOf(item.supplier.id), 1)">
                            <v-icon small>mdi-checkbox-marked</v-icon>
                          </v-btn>
                          <v-btn v-if="!preferredSuppliers.includes(item.supplier.id)" icon small @click="preferredSuppliers.push(item.supplier.id)">
                            <v-icon small>mdi-checkbox-blank-outline</v-icon>
                          </v-btn>
                          <v-btn icon small @click="preferredSuppliers = [item.supplier.id]">
                            <v-icon small>mdi-check-decagram</v-icon>
                          </v-btn>
                        </td>
                        <td class="text-center"><DiscountHook v-model="suppliersDiscount[item.supplier.id]"/></td>
                        <td class="text-right">{{ item.articles.length }}</td>
                        <td class="text-right">{{ item.selected.length }}</td>
                        <td v-if="item.currency" class="text-right">{{ (item.total - item.excess).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2, currency: item.currency, currencyDisplay: 'symbol', style: 'currency' }) }}</td>
                        <td v-else class="text-center">-</td>
                        <td v-if="item.currency" class="text-right">{{ item.excess.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2, currency: item.currency, currencyDisplay: 'symbol', style: 'currency' }) }}</td>
                        <td v-else class="text-center">-</td>
                        <td v-if="item.currency" class="text-right">{{ item.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2, currency: item.currency, currencyDisplay: 'symbol', style: 'currency' }) }}</td>
                        <td v-else class="text-center">-</td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr>
                        <th>Total</th>
                        <th></th>
                        <th class="text-center">{{ preferredSuppliers.length }}</th>
                        <th class="text-right">{{ supplierArticles.reduce((acc, line) => acc + line.articles.length, 0) }}</th>
                        <th class="text-right">{{ supplierArticles.reduce((acc, line) => acc + line.selected.length, 0) }}</th>
                        <th class="text-right">{{ (supplierArticles.reduce((acc, line) => acc + line.totalNormalized, 0) - supplierArticles.reduce((acc, line) => acc + line.excessNormalized, 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</th>
                        <th class="text-right">{{ supplierArticles.reduce((acc, line) => acc + line.excessNormalized, 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</th>
                        <th class="text-right">{{ supplierArticles.reduce((acc, line) => acc + line.totalNormalized, 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</th>
                      </tr>
                    </tfoot>
                  </template>
                </v-simple-table>
              </v-col>
              <v-col cols="12" sm="4" xl="2" v-if="supplierArticles.reduce((acc, supplier) => supplier.articles.length, 0)">
                <ChartBOMSupplierBreakdown
                  title="Response count"
                  :labels="supplierArticles.map(supplier => supplier.supplier.name)"
                  :items="supplierArticles.map(supplier => supplier.articles.length)
                "/>
              </v-col>
              <v-col cols="12" sm="4" xl="2" v-if="supplierArticles.reduce((acc, supplier) => supplier.articles.length, 0)">
                <ChartBOMSupplierBreakdown
                  title="Selected line count"
                  :labels="supplierArticles.map(supplier => supplier.supplier.name)"
                  :items="supplierArticles.map(supplier => supplier.selected.length)
                "/>
              </v-col>
              <v-col cols="12" sm="4" xl="2" v-if="supplierArticles.reduce((acc, supplier) => supplier.articles.length, 0)">
                <ChartBOMSupplierBreakdown
                  title="Total cost"
                  :labels="supplierArticles.map(supplier => supplier.supplier.name)"
                  :items="supplierArticles.map(supplier => supplier.totalNormalized)
                "/>
              </v-col>
            </v-row>
          </v-container>
          <h3>BOM line details</h3>
          <v-data-table
            :headers="headers"
            :items="bom"
            :items-per-page="-1"
            show-expand
            :loading="loadingInProgress"
            class="elevation-4 mx-1 mt-4 mb-1"
          >
            <template v-slot:item.quantity="{ item }">
              {{ (item.quantity * multiplier).toLocaleString(undefined, { maximumFractionDigits: 0}) }}
            </template>
            <template v-slot:item.price="{ item }">
              <span v-if="item.price && item.price.linePrice">
                {{ (item.price.linePrice - item.price.excessCost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2, currency: item.price.currency, currencyDisplay: 'symbol', style: 'currency' }) }}
              </span>
            </template>
            <template v-slot:item.excess="{ item }">
              <span v-if="item.price && item.price.excessCost">
                {{ item.price.excessCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2, currency: item.price.currency, currencyDisplay: 'symbol', style: 'currency' }) }}
              </span>
            </template>
            <template v-slot:item.price.matchPriority="{ item }">
              <MatchCircleIcon
                v-if="item.price && item.price.article"
                :partNumber="item.price.article.manufacturerPartNumber"
                :query="item.partNumber"
              />
            </template>
            <template v-slot:item.price.article.update.stock="{ item }">
              <StockGaugeIcon
                v-if="item.price && item.price.article"
                :stock="item.price.article.update.stock"
                :quantity="item.quantity * multiplier"
              />
            </template>
            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length" class="pa-4" v-if="item.articles.length > 0">
                <v-simple-table>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-center">SKU</th>
                        <th class="text-center">Supplier</th>
                        <th class="text-center">Manufacturer</th>
                        <th class="text-center">Part number</th>
                        <th class="text-center">Match</th>
                        <th class="text-center">Packaging</th>
                        <th class="text-center">Stock</th>
                        <th class="text-center">Price</th>
                        <th class="text-center">Select</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(article, key) in item.articles" :key="key">
                        <td>
                          <router-link :to="{ name: 'ArticlesDetail', params: { id: article.id } }">
                            {{ article.sku }}
                          </router-link>
                        </td>
                        <td>{{ article.supplier.name }}</td>
                        <td>{{ article.manufacturerName }}</td>
                        <td>
                          {{ article.manufacturerPartNumber }}
                        </td>
                        <td>
                          <MatchCircleIcon :partNumber="article.manufacturerPartNumber" :query="item.partNumber"/>
                        </td>
                        <td>{{ article.packaging }}</td>
                        <td>{{ article.update.stock }}</td>
                        <td>
                          <span v-if="item.articlesPrice[key].linePrice">
                            {{ item.articlesPrice[key].linePrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2, currency: item.articlesPrice[key].currency, currencyDisplay: 'symbol', style: 'currency' }) }}
                          </span>
                        </td>
                        <td>
                          <v-btn
                            icon
                            @click="changePriceSelected(item.id, key)"
                          >
                            <v-icon v-if="item.price && article.id === item.price.article.id">mdi-checkbox-marked</v-icon>
                            <v-icon v-else>mdi-checkbox-blank-outline</v-icon>
                          </v-btn>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </td>
              <td :colspan="headers.length" class="pa-4" v-else>Nothing!</td>
            </template>
          </v-data-table>
        </v-stepper-content>
        <v-stepper-content step="5">
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>
  </v-container>
</template>

<script>
import XLSX from 'xlsx'
import MessagePopup from '@/components/MessagePopup.vue'
import { mapState } from 'vuex'
import { bestPriceFromUpdate, preferredOptionFromArticleList, checkPartNumberMatching } from '@/helpers/prices'
import ChartBOMSupplierBreakdown from '@/components/ChartBOMSupplierBreakdown'
import MatchCircleIcon from '@/components/MatchCircleIcon'
import StockGaugeIcon from '@/components/StockGaugeIcon'
import CopyToDGKCart from '@/components/CopyToDGKCart'
import DiscountHook from '@/components/DiscountHook'

export default {
  data: () => ({
    step: 1,
    errorLoading: '',
    file: null,
    bom: [],
    fileContent: [],
    fileColumnMapping: {
      customerPartNumber: null,
      partNumber: null,
      quantity: null
    },
    headers: [
      { text: 'CPN', value: 'customerPartNumber' },
      { text: 'PN', value: 'partNumber' },
      { text: 'Qu', value: 'quantity', align: 'right' },
      { text: 'Status', value: 'status', align: 'right' },
      { text: 'Selected', value: 'price.article.supplier.name', align: 'center' },
      { text: 'Match', value: 'price.matchPriority', align: 'center' },
      { text: 'Stock', value: 'price.article.update.stock', align: 'center' },
      { text: 'Net price', value: 'price', align: 'right' },
      { text: 'Excess', value: 'excess', align: 'right' },
      { text: 'Details', value: 'data-table-expand' }
    ],
    errorMessage: '',
    multiplier: 1,
    multiplierLog: 0,
    preferredSuppliers: [],
    dateExpected: new Date().toISOString().substring(0, 10),
    priceOnly: true,
    menuDateExpected: false,
    suppliersDiscount: []
  }),
  created () {
    this.preferredSuppliers = this.suppliers.filter(supplier => supplier.enabled).map(supplier => supplier.id)
  },
  components: {
    MessagePopup,
    ChartBOMSupplierBreakdown,
    MatchCircleIcon,
    StockGaugeIcon,
    CopyToDGKCart,
    DiscountHook
  },
  methods: {
    ...{ checkPartNumberMatching, bestPriceFromUpdate },
    loadFile (file) {
      this.errorLoading = ''
      if (file) {
        const reader = new FileReader()
        reader.onload = (e) => {
          const data = new Uint8Array(e.target.result)
          const workbook = XLSX.read(data, { type: 'array' })
          if (workbook.SheetNames.length > 0) {
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]]
            this.fileContent = XLSX.utils.sheet_to_json(firstSheet, { header: 1 })
            this.fileContent.forEach((item, index) => {
              item.splice(0, 0, index + 1)
            })
            this.step = 2
          } else {
            this.errorLoading = 'No sheet has been found.'
          }
        }
        reader.onerror = () => {
          this.errorLoading = 'Error while loading the file.'
        }
        reader.readAsArrayBuffer(file)
      }
    },
    createBom () {
      this.bom = this.fileContentSanitized
        .map((item, id) => ({
          id,
          customerPartNumber: !this.fileColumnMapping.customerPartNumber ? undefined : item[this.fileColumnMapping.customerPartNumber - 1],
          partNumber: !this.fileColumnMapping.partNumber ? undefined : String(item[this.fileColumnMapping.partNumber - 1]),
          quantity: !this.fileColumnMapping.quantity ? undefined : parseInt(item[this.fileColumnMapping.quantity - 1]),
          status: 'loading',
          articles: []
        }))
      this.step = 3
      this.searchAll()
    },
    searchSupplier (id, supplier, query, exact) {
      return this.$http(this.$directory + `/suppliers/${supplier.id}/articles/search`, {
        params: {
          query: query,
          exact: exact
        }
      })
        .then(response => {
          if (response.status === 200) {
            response.data['hydra:member'].forEach(item => {
              const article = item.article
              delete item.article
              article.update = item
              this.bom[id].articles.push(article)
            })
          }
        })
        .catch(error => {
          if (!error.response) {
            this.errorMessage = `Supplier ${supplier.name}: ${error.message}`
          } else {
            this.errorMessage = `Supplier ${supplier.name}: ${error.response.data['hydra:description'].replace(/\n/g, '<br/>')}`
          }
        })
    },
    async searchAll () {
      this.errorMessage = ''
      for (const [id, line] of this.bom.entries()) {
        line.articles = []
        if (line.partNumber && line.quantity > 0) {
          const promisesArray = []
          this.suppliers
            .filter(supplier => supplier.enabled)
            .forEach(supplier => {
              promisesArray.push(this.searchSupplier(id, supplier, line.partNumber, false))
            })
          await Promise.all(promisesArray)
            .then(() => {
              if (line.articles.length > 0) {
                line.status = line.articles.length + ' results'
              } else {
                line.status = 'nothing'
              }
            })
            .catch(() => {
              line.status = 'error'
            })
            .finally(() => {
              this.$set(line, 'price', preferredOptionFromArticleList(line.articles, line.quantity * this.multiplier, line.partNumber, this.preferredSuppliers, this.priceOnly ? null : new Date(this.dateExpected), this.suppliersDiscount))
              this.$set(line, 'articlesPrice', line.articles.map(article => bestPriceFromUpdate(article.update, line.quantity * this.multiplier, article.supplier.exchangeRate, this.suppliersDiscount[article.supplier.id])))
            })
        } else {
          line.status = 'incomplete'
        }
      }
    },
    changePriceSelected (id, articleIndex) {
      const article = this.bom[id].articles[articleIndex]
      this.$set(this.bom[id], 'price', { ...bestPriceFromUpdate(article.update, this.bom[id].quantity * this.multiplier, article.supplier.exchangeRate, this.suppliersDiscount[article.supplier.id]), ...{ article } })
    },
    selectAllBestPrices () {
      this.bom.forEach(line => {
        this.$set(line, 'price', preferredOptionFromArticleList(line.articles, line.quantity * this.multiplier, line.partNumber, this.preferredSuppliers, this.priceOnly ? null : new Date(this.dateExpected), this.suppliersDiscount))
        this.$set(line, 'articlesPrice', line.articles.map(article => bestPriceFromUpdate(article.update, line.quantity * this.multiplier, article.supplier.exchangeRate, this.suppliersDiscount[article.supplier.id])))
      })
    }
  },
  computed: {
    ...mapState(['suppliers']),
    fileContentCols () {
      if (this.fileContent.length > 0) {
        return this.fileContent.reduce((maxCols, item) => item.length > maxCols ? item.length : maxCols, 0)
      }
      return 0
    },
    fileContentSanitized () {
      return this.fileContent
        .filter(item => item.length > 0)
        .map((item, index) => {
          if (item.length < this.fileContentCols) {
            item = item.concat(Array(this.fileContentCols - item.length))
          }
          return item
        })
    },
    loadingInProgress () {
      return this.bom.filter(line => line.status === 'loading').length > 0
    },
    multiplierString () {
      if (this.multiplier < 1000) {
        return this.multiplier.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
      } else {
        return (this.multiplier / 1000).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 1 }) + 'k'
      }
    },
    supplierArticles () {
      const supplierArticles = []
      this.suppliers
        .filter(supplier => supplier.enabled)
        .forEach(supplier => {
          const articles = []
          const selected = []
          let total = 0
          let totalNormalized = 0
          let excess = 0
          let excessNormalized = 0
          let currency = ''
          this.bom.forEach(line => {
            line.articles
              .filter(article => article.supplier.id === supplier.id)
              .forEach(article => articles.push(article))
            if (line.price) {
              const selectedArticle = line.articles.find(article => article.id === line.price.article.id)
              if (selectedArticle.supplier.id === supplier.id) {
                selected.push({ linePrice: line.price, articles: selectedArticle })
                total += line.price.linePrice
                totalNormalized += line.price.linePriceNormalized
                excess += line.price.excessCost
                excessNormalized += line.price.excessCostNormalized
                currency = selectedArticle.update.currency
              }
            }
          })
          supplierArticles.push({ supplier, articles, selected, total, excess, totalNormalized, excessNormalized, currency })
        })
      return supplierArticles
    }
  },
  watch: {
    step () {
      if (this.step <= 1) {
        this.file = null
        this.fileContent = []
      }
      if (this.step <= 2) {
        this.bom = []
      }
    },
    multiplier () {
      this.selectAllBestPrices()
    },
    multiplierLog () {
      const units = [1, 2.5, 5, 7.5]
      const unit = units[this.multiplierLog % units.length]
      const multiplicator = Math.floor(this.multiplierLog / units.length)
      this.multiplier = parseInt(unit * Math.pow(10, multiplicator))
    },
    preferredSuppliers () {
      this.selectAllBestPrices()
    },
    priceOnly () {
      this.selectAllBestPrices()
    },
    dateExpected () {
      this.selectAllBestPrices()
    },
    suppliersDiscount () {
      this.selectAllBestPrices()
    }
  }
}
</script>
